import { useEffect, useRef, useState } from 'react'

/* ═══════════════════════════════════════════════════════════════════════════
   Animation primitives for the Dizee marketing pages.
   Pure CSS + Intersection Observer. No heavy dependencies.
   ═══════════════════════════════════════════════════════════════════════════ */

/**
 * FadeIn — reveals children on scroll with configurable direction + delay.
 */
export function FadeIn({
  children,
  className = '',
  delay = 0,
  direction = 'up',
  duration = 700,
  once = true,
}: {
  children: React.ReactNode
  className?: string
  delay?: number
  direction?: 'up' | 'down' | 'left' | 'right' | 'none'
  duration?: number
  once?: boolean
}) {
  const ref = useRef<HTMLDivElement>(null)
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    const el = ref.current
    if (!el) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true)
          if (once) observer.unobserve(el)
        } else if (!once) {
          setVisible(false)
        }
      },
      { threshold: 0.15, rootMargin: '0px 0px -40px 0px' },
    )

    observer.observe(el)
    return () => observer.disconnect()
  }, [once])

  const transforms: Record<string, string> = {
    up: 'translateY(24px)',
    down: 'translateY(-24px)',
    left: 'translateX(24px)',
    right: 'translateX(-24px)',
    none: 'none',
  }

  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'none' : transforms[direction],
        transition: `opacity ${duration}ms cubic-bezier(0.16, 1, 0.3, 1) ${delay}ms, transform ${duration}ms cubic-bezier(0.16, 1, 0.3, 1) ${delay}ms`,
        willChange: 'opacity, transform',
      }}
    >
      {children}
    </div>
  )
}

/**
 * TypeWriter — types out lines of text character by character.
 * Starts when scrolled into view.
 */
export function TypeWriter({
  lines,
  className = '',
  charSpeed = 25,
  lineDelay = 400,
  startDelay = 300,
}: {
  lines: { text: string; className?: string; prefix?: string; prefixClassName?: string }[]
  className?: string
  charSpeed?: number
  lineDelay?: number
  startDelay?: number
}) {
  const ref = useRef<HTMLDivElement>(null)
  const [started, setStarted] = useState(false)
  const [displayLines, setDisplayLines] = useState<string[]>(lines.map(() => ''))
  const [currentLine, setCurrentLine] = useState(0)
  const [currentChar, setCurrentChar] = useState(0)
  const [showCursor, setShowCursor] = useState(true)

  useEffect(() => {
    const el = ref.current
    if (!el) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started) {
          setTimeout(() => setStarted(true), startDelay)
          observer.unobserve(el)
        }
      },
      { threshold: 0.3 },
    )

    observer.observe(el)
    return () => observer.disconnect()
  }, [started, startDelay])

  useEffect(() => {
    if (!started) return
    if (currentLine >= lines.length) return

    const line = lines[currentLine]
    if (currentChar < line.text.length) {
      const timeout = setTimeout(() => {
        setDisplayLines((prev) => {
          const next = [...prev]
          next[currentLine] = line.text.slice(0, currentChar + 1)
          return next
        })
        setCurrentChar((c) => c + 1)
      }, charSpeed)
      return () => clearTimeout(timeout)
    } else {
      const timeout = setTimeout(() => {
        setCurrentLine((l) => l + 1)
        setCurrentChar(0)
      }, lineDelay)
      return () => clearTimeout(timeout)
    }
  }, [started, currentLine, currentChar, lines, charSpeed, lineDelay])

  useEffect(() => {
    const interval = setInterval(() => setShowCursor((c) => !c), 530)
    return () => clearInterval(interval)
  }, [])

  return (
    <div ref={ref} className={className}>
      {lines.map((line, i) => (
        <p key={i} className={line.className || ''} style={{ minHeight: '1.75rem' }}>
          {line.prefix && (
            <span className={line.prefixClassName || ''}>{line.prefix}</span>
          )}
          <span>{displayLines[i]}</span>
          {i === currentLine && started && currentLine < lines.length && (
            <span
              className="inline-block w-[2px] h-[14px] bg-sky-400 ml-[1px] align-middle"
              style={{ opacity: showCursor ? 1 : 0 }}
            />
          )}
        </p>
      ))}
    </div>
  )
}

/**
 * GlowOrb — a slowly moving ambient glow for backgrounds.
 */
export function GlowOrb({
  color = 'rgba(56, 189, 248, 0.12)',
  size = 600,
  className = '',
  duration = 20,
}: {
  color?: string
  size?: number
  className?: string
  duration?: number
}) {
  return (
    <div
      className={`pointer-events-none absolute rounded-full blur-3xl ${className}`}
      style={{
        width: size,
        height: size,
        background: `radial-gradient(circle, ${color} 0%, transparent 70%)`,
        animation: `float ${duration}s ease-in-out infinite`,
      }}
    />
  )
}

/**
 * CountUp — animates a number from 0 to target on scroll.
 */
export function CountUp({
  target,
  duration = 2000,
  prefix = '',
  suffix = '',
  className = '',
}: {
  target: number
  duration?: number
  prefix?: string
  suffix?: string
  className?: string
}) {
  const ref = useRef<HTMLSpanElement>(null)
  const [value, setValue] = useState(0)
  const [started, setStarted] = useState(false)

  useEffect(() => {
    const el = ref.current
    if (!el) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started) {
          setStarted(true)
          observer.unobserve(el)
        }
      },
      { threshold: 0.5 },
    )

    observer.observe(el)
    return () => observer.disconnect()
  }, [started])

  useEffect(() => {
    if (!started) return

    const steps = 60
    const stepDuration = duration / steps
    let step = 0

    const interval = setInterval(() => {
      step++
      const progress = step / steps
      const eased = 1 - Math.pow(1 - progress, 3)
      setValue(Math.round(eased * target))

      if (step >= steps) {
        setValue(target)
        clearInterval(interval)
      }
    }, stepDuration)

    return () => clearInterval(interval)
  }, [started, target, duration])

  return (
    <span ref={ref} className={className}>
      {prefix}{value.toLocaleString()}{suffix}
    </span>
  )
}

/**
 * Shimmer — a horizontal shimmer/shine effect on text or elements.
 */
export function Shimmer({
  children,
  className = '',
}: {
  children: React.ReactNode
  className?: string
}) {
  return (
    <span className={`relative inline-block overflow-hidden ${className}`}>
      {children}
      <span
        className="absolute inset-0 -translate-x-full"
        style={{
          background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.08), transparent)',
          animation: 'shimmer 3s ease-in-out infinite',
        }}
      />
    </span>
  )
}
